/**
 * MultiAddResultStep — terminal step of the log-activity multi-add flow.
 *
 * The Entry step persisted activities upstream (handing back BulkImport ids);
 * the Evidence step optionally attached files. By the time we render here
 * the work is already done — this screen confirms what landed and routes
 * the user onward.
 *
 * Mirrors `ActivityResultStep`'s visual structure but aggregates over the
 * batch: total CO₂e summed across the imported activities, calculation
 * status counts, and the same evidence list.
 */

import { useMemo } from 'react';
import { CheckCircle, AlertTriangle, ExternalLink, List } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  CalculationStatus,
  type EmissionEvidence,
} from '../../../types/emissions';
import { EVIDENCE_TYPE_LABEL, getMimeIcon } from '../../../utils/evidenceDisplay';
import { useGetActivitiesQuery } from '../../../api/emissions';
import type { FormattedEmission } from '../../../utils/emissionsFormatter';

const PAGE_SIZE = 100;

interface MultiAddResultStepProps {
  initiativeId: string;
  /** BulkImport ids returned by the Entry step's submit. */
  importIds: string[];
  /** Evidences linked to the batch in the preceding Attach step. */
  attachedEvidences: EmissionEvidence[];
  formatCompact: (valueInKg: number | null | undefined) => FormattedEmission;
  onViewActivities: () => void;
  onAddAnother: () => void;
  /**
   * Jump straight to a single activity's detail page. The wizard
   * promotes this to the primary CTA when only one activity was
   * created, since "View Activities" → list page is one extra click
   * to land where the user almost certainly wants to be.
   */
  onViewActivity?: (activityId: string) => void;
}

export const MultiAddResultStep = ({
  initiativeId,
  importIds,
  attachedEvidences,
  formatCompact,
  onViewActivities,
  onAddAnother,
  onViewActivity,
}: MultiAddResultStepProps) => {
  const { data, isFetching } = useGetActivitiesQuery(
    {
      initiativeId,
      bulkImportIds: importIds.join(','),
      limit: PAGE_SIZE,
      offset: 0,
    },
    { skip: importIds.length === 0 },
  );

  const activities = data?.data ?? [];
  const totalCount = data?.pagination?.total ?? activities.length;

  const { totalCo2e, calculatedCount, failedCount, pendingCount } = useMemo(() => {
    let total = 0;
    let calculated = 0;
    let failed = 0;
    let pending = 0;
    for (const a of activities) {
      if (a.calculationStatus === CalculationStatus.Calculated && typeof a.calculatedCO2e === 'number') {
        total += a.calculatedCO2e;
        calculated += 1;
      } else if (a.calculationStatus === CalculationStatus.Failed) {
        failed += 1;
      } else {
        pending += 1;
      }
    }
    return { totalCo2e: total, calculatedCount: calculated, failedCount: failed, pendingCount: pending };
  }, [activities]);

  const hasIssues = failedCount > 0 || pendingCount > 0;
  const totalFormatted = formatCompact(totalCo2e);

  const uniqueEvidences = useMemo(
    () => Array.from(new Map(attachedEvidences.map((ev) => [ev._id, ev])).values()),
    [attachedEvidences],
  );

  return (
    <Card>
      <CardContent className='tw:p-8 tw:text-center'>
        <div
          className={cn(
            'tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-4',
            hasIssues ? 'tw:bg-amber-100' : 'tw:bg-emerald-100',
          )}
          style={{ width: 64, height: 64 }}
        >
          {hasIssues ? (
            <AlertTriangle className='tw:size-8 tw:text-amber-600' />
          ) : (
            <CheckCircle className='tw:size-8 tw:text-emerald-600' />
          )}
        </div>

        <h5 className='tw:font-semibold tw:mb-2'>
          {totalCount} {totalCount === 1 ? 'Activity' : 'Activities'} Created
        </h5>

        {/* Summed CO2e — only meaningful when at least one calculated */}
        {calculatedCount > 0 && (
          <div className='tw:mb-4'>
            <span className='tw:text-3xl tw:font-bold'>{totalFormatted.formatted}</span>
            <span className='tw:text-muted-foreground tw:ml-2'>{totalFormatted.unitCompact}</span>
            {calculatedCount < totalCount && (
              <p className='tw:text-xs tw:text-muted-foreground tw:mt-1 tw:mb-0'>
                Sum across {calculatedCount} of {totalCount} activities
              </p>
            )}
          </div>
        )}

        {isFetching && totalCount === 0 && (
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>Loading results…</p>
        )}

        {/* Status breakdown — only shown when something needs attention */}
        {hasIssues && (
          <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-amber-800 tw:text-sm tw:mb-2 tw:flex tw:items-center tw:gap-2'>
              <AlertTriangle className='tw:size-4' />
              Some activities need attention
            </h6>
            <ul className='tw:text-sm tw:text-amber-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {failedCount > 0 && (
                <li>{failedCount} {failedCount === 1 ? 'activity has' : 'activities have'} a failed calculation — open the activities list to review.</li>
              )}
              {pendingCount > 0 && (
                <li>{pendingCount} {pendingCount === 1 ? 'activity is' : 'activities are'} still calculating.</li>
              )}
            </ul>
          </div>
        )}

        {/* Attached evidence — confirms the Attach step landed. */}
        {uniqueEvidences.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-border tw:p-4 tw:mb-4 tw:text-left'>
            <h6 className='tw:font-semibold tw:text-sm tw:mb-2'>
              Evidence attached ({uniqueEvidences.length})
            </h6>
            <ul className='tw:divide-y tw:divide-border tw:text-sm'>
              {uniqueEvidences.map((ev) => (
                <li key={ev._id} className='tw:flex tw:items-center tw:gap-2 tw:py-2'>
                  <i className={`fa-regular ${getMimeIcon(ev.mimeType)} tw:text-ThemeTextMedium`} aria-hidden />
                  <span className='tw:truncate tw:flex-1 tw:min-w-0'>{ev.originalName}</span>
                  <Badge variant='ghost' className='tw:text-[10px]'>
                    {EVIDENCE_TYPE_LABEL[ev.evidenceType]}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/*
         * Actions. When exactly one activity was created we promote
         * "View Activity" (jump to its detail page) — the user almost
         * always wants to land on the thing they just created, not on
         * a list page where they'd have to click again. With more than
         * one activity, "View Activities" is the primary CTA.
         */}
        {(() => {
          const singleActivity = activities.length === 1 ? activities[0] : null;
          return (
            <div className='tw:flex tw:justify-center tw:gap-3 tw:flex-wrap'>
              <Button variant='outline' onClick={onAddAnother}>
                Add Another
              </Button>
              {singleActivity && onViewActivity ? (
                <>
                  <Button variant='outline' onClick={onViewActivities}>
                    <List className='tw:size-4' />
                    View Activities
                  </Button>
                  <Button onClick={() => onViewActivity(singleActivity._id)}>
                    <ExternalLink className='tw:size-4' />
                    View Activity
                  </Button>
                </>
              ) : (
                <Button onClick={onViewActivities}>
                  <List className='tw:size-4' />
                  View Activities
                </Button>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
};
